import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import posthog from 'posthog-js'
import Header from 'components/headers/withGreenBack'
import DotsLoading from 'components/loadings/dotsLoading'
import { SectionError } from 'components/states'
import { faPlain, toman } from 'utilities/helper/format'
import { initManualCashout, payCashboxWinners } from 'services/cashbox'
import {
    Card,
    Notice,
    PrimaryButton,
    SecondaryButton,
    apiMessage,
    inputClass,
} from 'modules/cashbox/ui'
import { CASHBOX_KEY, useCashbox } from 'modules/cashbox/hooks'

interface Payee {
    key: string
    name: string
    memberId: number
    cycleId: number
    amount: number
    /** the share group this payee belongs to, for display */
    groupName?: string
}

/**
 * پرداخت وام برندگان — the manager settling the last draw.
 *
 * Current app versions draw with `create_cashouts=False`, so a draw records
 * the winners but moves no money: the loan sits in the box's Hamyan balance
 * until the manager pays it out here. Until that happens the winner cannot
 * claim anything (`claim-cashbox-prize` requires `membership.have_cashout`),
 * which is why this screen is the step that closes the cycle.
 *
 * A share-group winner is paid as several transactions, one per member, split
 * by `share_residue`. The split is computed here rather than server-side
 * because `create_cashout_for_winner` does it in floating point into an
 * IntegerField; doing it in integers and giving the remainder to the largest
 * holder keeps the parts summing to exactly the loan.
 *
 * No `gateway` is sent: the source is the box balance (`hbb`), which never
 * touches a Gateway — and Android's habit of stuffing a default gateway id in
 * here is the reason the backend has a compatibility shim for it.
 */
export default function CashboxPayWinnersPage() {
    const { id } = useParams()
    const navigate = useNavigate()
    const queryClient = useQueryClient()

    const { cashbox, isLoading, isError, refetch } = useCashbox(id)
    const { data: cashoutInit, isLoading: initLoading } = useQuery(
        ['cashbox-cashout-init', id],
        () => initManualCashout(id!),
        { enabled: Boolean(id), retry: false }
    )

    const [accountByMember, setAccountByMember] = useState<
        Record<number, number>
    >({})

    const payees: Array<Payee> = useMemo(() => {
        const winners = cashbox?.last_cycle_unpaid_winners ?? []
        const shareValue = cashbox?.period?.share_value ?? 0
        const rows: Array<Payee> = []

        winners.forEach((winner, index) => {
            if (winner.membership) {
                rows.push({
                    key: `m-${index}-${winner.membership.id}`,
                    name:
                        winner.membership.name?.trim() ||
                        winner.membership.phone_number ||
                        'برنده',
                    memberId: winner.membership.member_id,
                    cycleId: winner.cycle,
                    amount: winner.loan_amount,
                })
                return
            }
            if (winner.share_group) {
                const members = winner.share_group.memberships ?? []
                // Integer split; the largest holder absorbs the remainder so
                // the parts add up to the loan exactly.
                const parts = members.map((member) =>
                    shareValue > 0
                        ? Math.floor(
                              (winner.loan_amount * member.share_residue) /
                                  shareValue
                          )
                        : 0
                )
                const remainder =
                    winner.loan_amount - parts.reduce((a, b) => a + b, 0)
                if (remainder > 0 && parts.length > 0) {
                    let biggest = 0
                    members.forEach((member, i) => {
                        if (
                            member.share_residue >
                            members[biggest].share_residue
                        )
                            biggest = i
                    })
                    parts[biggest] += remainder
                }
                members.forEach((member, i) => {
                    rows.push({
                        key: `g-${index}-${member.id}`,
                        name:
                            member.name?.trim() ||
                            member.phone_number ||
                            'عضو گروه',
                        memberId: member.member_id,
                        cycleId: winner.cycle,
                        amount: parts[i],
                        groupName: winner.share_group!.name,
                    })
                })
            }
        })
        return rows
    }, [cashbox])

    const accountsByMember = useMemo(() => {
        const map = new Map<number, Array<any>>()
        ;(cashoutInit?.bank_accounts ?? []).forEach((account: any) => {
            const bucket = map.get(account.member) ?? []
            bucket.push(account)
            map.set(account.member, bucket)
        })
        return map
    }, [cashoutInit])

    const total = payees.reduce((sum, payee) => sum + payee.amount, 0)
    const missingAccount = payees.filter(
        (payee) => !accountByMember[payee.memberId]
    )
    const overBalance = total > (cashbox?.hamyan_balance ?? 0)

    const pay = useMutation({
        mutationFn: () =>
            payCashboxWinners({
                isWorried: cashbox?.trust_state !== 'trst',
                items: payees.map((payee) => ({
                    cycle_id: payee.cycleId,
                    amount: payee.amount,
                    receiver_id: payee.memberId,
                    bank_account_id: accountByMember[payee.memberId],
                })),
            }),
        onSuccess: () => {
            // The step that actually closes a cycle: until this succeeds the
            // winner cannot claim anything, so a drop-off here strands money in
            // the box balance with a winner waiting on it.
            posthog.capture('cashbox_pay_winners_succeeded', {
                cashbox_id: id,
                winners_count: payees.length,
                total,
            })
            toast.success('پرداخت وام برندگان ثبت شد')
            queryClient.invalidateQueries(CASHBOX_KEY(id))
            queryClient.invalidateQueries(['get-boxes'])
            navigate(`/cashbox/${id}`)
        },
        onError: (error: any) => {
            posthog.capture('cashbox_pay_winners_failed', {
                cashbox_id: id,
                winners_count: payees.length,
                total,
                status: error?.response?.status,
            })
            toast.error(apiMessage(error))
        },
    })

    if (isLoading || initLoading) return <DotsLoading />
    if (isError || !cashbox)
        return (
            <div className="bg-white min-h-screen">
                <Header title="پرداخت وام برندگان" />
                <div className="p-4">
                    <SectionError onRetry={() => refetch()} />
                </div>
            </div>
        )

    if (cashbox.membership?.role !== 'own')
        return (
            <div className="bg-white min-h-screen">
                <Header title="پرداخت وام برندگان" />
                <div className="p-4">
                    <Notice tone="warn">
                        فقط مدیر صندوق می‌تواند وام برندگان را پرداخت کند.
                    </Notice>
                </div>
            </div>
        )

    if (payees.length === 0)
        return (
            <div className="bg-white min-h-screen">
                <Header title="پرداخت وام برندگان" />
                <div className="p-4">
                    <Notice tone="info">
                        برنده‌ی پرداخت‌نشده‌ای وجود ندارد.
                    </Notice>
                    <div className="mt-3">
                        <SecondaryButton
                            onClick={() => navigate(`/cashbox/${id}`)}
                        >
                            بازگشت به صندوق
                        </SecondaryButton>
                    </div>
                </div>
            </div>
        )

    return (
        <div className="bg-[#FAFAFA] min-h-screen pb-8">
            <div className="bg-white">
                <Header title="پرداخت وام برندگان" />
            </div>

            <div className="px-4 pt-3">
                <Card className="mb-3">
                    <div className="flex justify-between items-baseline">
                        <span className="text-sm text-text-secondary">
                            موجودی قابل پرداخت
                        </span>
                        <span className="text-sm font-bold text-black">
                            {toman(cashbox.hamyan_balance)}
                        </span>
                    </div>
                    <div className="flex justify-between items-baseline mt-2">
                        <span className="text-sm text-text-secondary">
                            جمع پرداختی
                        </span>
                        <span className="text-sm font-bold text-black">
                            {toman(total)}
                        </span>
                    </div>
                </Card>

                {payees.map((payee) => {
                    const accounts = accountsByMember.get(payee.memberId) ?? []
                    return (
                        <Card key={payee.key} className="mb-2">
                            <div className="flex justify-between items-start">
                                <div className="min-w-0">
                                    <p className="text-sm font-bold text-black truncate">
                                        {payee.name}
                                    </p>
                                    {payee.groupName && (
                                        <p className="text-xs text-text-secondary mt-0.5">
                                            از گروه «{payee.groupName}»
                                        </p>
                                    )}
                                </div>
                                <p className="text-sm font-bold text-black shrink-0 pl-1">
                                    {toman(payee.amount)}
                                </p>
                            </div>

                            <div className="mt-2">
                                {accounts.length === 0 ? (
                                    <p className="text-xs text-[#B3261E]">
                                        این عضو حساب بانکی تاییدشده‌ای ثبت نکرده
                                        است؛ تا ثبت حساب، پرداخت ممکن نیست.
                                    </p>
                                ) : (
                                    <select
                                        value={
                                            accountByMember[payee.memberId] ??
                                            ''
                                        }
                                        onChange={(event) =>
                                            setAccountByMember((prev) => ({
                                                ...prev,
                                                [payee.memberId]: Number(
                                                    event.target.value
                                                ),
                                            }))
                                        }
                                        className={inputClass}
                                    >
                                        <option value="">
                                            انتخاب حساب بانکی…
                                        </option>
                                        {accounts.map((account: any) => (
                                            <option
                                                key={account.id}
                                                value={account.id}
                                            >
                                                {account.bank_name} ·{' '}
                                                {account.owner_name}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </div>
                        </Card>
                    )
                })}

                {(missingAccount.length > 0 || overBalance) && (
                    <div className="mt-3">
                        <Notice tone="warn">
                            {missingAccount.length > 0 && (
                                <>
                                    • برای {faPlain.format(
                                        missingAccount.length
                                    )}{' '}
                                    نفر حساب بانکی انتخاب نشده است
                                    <br />
                                </>
                            )}
                            {overBalance && (
                                <>
                                    • جمع پرداختی از موجودی صندوق بیشتر است
                                    <br />
                                </>
                            )}
                        </Notice>
                    </div>
                )}

                <div className="mt-4">
                    <PrimaryButton
                        disabled={
                            missingAccount.length > 0 ||
                            overBalance ||
                            pay.isLoading
                        }
                        onClick={() => {
                            if (
                                !window.confirm(
                                    `مبلغ ${toman(
                                        total
                                    )} به برندگان پرداخت شود؟`
                                )
                            )
                                return
                            posthog.capture('cashbox_pay_winners_started', {
                                cashbox_id: id,
                                winners_count: payees.length,
                                total,
                            })
                            pay.mutate()
                        }}
                    >
                        {pay.isLoading ? 'در حال پرداخت…' : 'پرداخت'}
                    </PrimaryButton>
                </div>
            </div>
        </div>
    )
}
